import { useCallback, useEffect, useState } from 'react'
import { env } from '@/libs/env'
import { registerPushSubscription, unregisterPushSubscription } from '@/apis/aipass'
import type { PushNotificationPermission } from '@/models/push-notification'
import { fingerprint } from '@/utils/fingerprint'
import { reportError } from '@/components/apps/error-boundary'

const SW_PATH = '/sw-push.js'

/**
 * VAPID 公開鍵（Base64URL）を Web Push API が要求する Uint8Array に変換する。
 * PushManager.subscribe の applicationServerKey に渡すために必要。
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  // ArrayBuffer を明示することで Uint8Array<ArrayBuffer> 型にし、applicationServerKey の型制約を満たす
  const buffer = new ArrayBuffer(rawData.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; i++) {
    view[i] = rawData.charCodeAt(i)
  }
  return view
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** セルフチェックイン / セルフチェックアウトは宿泊者が操作する端末画面のため、Push 通知の対象外とする */
const PUSH_DISABLED_PATH_PATTERN = /^\/self-check(in|out)(\/|$)/

function isPushAvailable(): boolean {
  return isPushSupported() && !PUSH_DISABLED_PATH_PATTERN.test(window.location.pathname)
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH)
  const registration = existing ?? (await navigator.serviceWorker.register(SW_PATH))
  // 更新チェックは登録の破棄や端末のストレージ制限と競合して reject するが、購読状態には影響しないため握りつぶす
  registration.update().catch(() => {})
  return registration
}

/**
 * ブラウザ Push 通知の購読管理 hook。
 *
 * - Service Worker の登録
 * - Notification 権限リクエスト
 * - PushSubscription の取得 / 解除
 * - バックエンド API へのサブスクリプション登録 / 削除
 *
 * このhook 単体でも使えるが、通常は PushNotificationContext 経由で利用する。
 */
export const usePushNotification = () => {
  const [permission, setPermission] = useState<PushNotificationPermission>(
    isPushAvailable() ? (Notification.permission as PushNotificationPermission) : 'denied',
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)

  // マウント時に既存の購読状態を復元する
  useEffect(() => {
    if (!isPushAvailable()) {
      setIsInitializing(false)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const registration = await getRegistration()
        const subscription = await registration.pushManager.getSubscription()
        if (!cancelled) setIsSubscribed(!!subscription)
      } catch (e) {
        console.error('Push notification initialization failed', e)
      } finally {
        if (!cancelled) setIsInitializing(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const subscribe = useCallback(async () => {
    if (!isPushAvailable()) return

    const result = await Notification.requestPermission()
    setPermission(result as PushNotificationPermission)
    if (result !== 'granted') return

    const vapidPublicKey = env('VAPID_PUBLIC_KEY')
    const registration = await getRegistration()
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
    })

    try {
      const json = subscription.toJSON()
      await registerPushSubscription({
        fingerprint,
        endpoint: subscription.endpoint,
        keys: {
          p256dh: json.keys?.p256dh ?? '',
          auth: json.keys?.auth ?? '',
        },
      })
      setIsSubscribed(true)
    } catch (e) {
      reportError(e)
      await subscription.unsubscribe()
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    if (!isPushAvailable()) return

    const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
    if (!registration) return

    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    await subscription.unsubscribe()
    try {
      await unregisterPushSubscription(fingerprint)
    } catch (e) {
      reportError(e)
    } finally {
      setIsSubscribed(false)
    }
  }, [])

  return { permission, isSubscribed, isInitializing, subscribe, unsubscribe }
}
